import React, { useState } from "react";
import { CSVLink } from "react-csv";
import DataTable from "react-data-table-component";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import Swal from "sweetalert2";

const AnxietyList = ({ anxietyAssessments, isLoading, error, onAddAnxiety, onEditAnxiety, onDeleteAnxiety }) => {
    const [filterText, setFilterText] = useState("");
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);

    const records = anxietyAssessments || [];

    const filteredRecords = records.filter((r) =>
        Object.values(r).some((v) =>
            v != null && String(v).toLowerCase().includes(filterText.toLowerCase())
        )
    );

    const totalRows = filteredRecords.length;
    const start = (currentPage - 1) * rowsPerPage + 1;
    const end = Math.min(currentPage * rowsPerPage, totalRows);

    const handleDelete = (id) => {
        if (onDeleteAnxiety) onDeleteAnxiety(id);
    };

    const handleExportPDF = () => {
        const doc = new jsPDF();
        doc.text("Anxiety Form", 14, 10);
        const tableColumn = ["#", "Client Name", "Assessment Date", "Total Score", "Anxiety Level", "Assessed By"];
        const tableRows = filteredRecords.map((r, index) => [
            index + 1,
            r.client_name || "",
            r.assessment_date || "",
            r.total_score || 0,
            r.anxiety_level || "",
            r.assessed_by || ""
        ]);

        autoTable(doc, {
            head: [tableColumn],
            body: tableRows,
            startY: 20
        });
        doc.save("bai_assessments.pdf");
    };

    const CustomPagination = ({ onChangePage, onChangeRowsPerPage, currentPage, rowsPerPage, rowCount }) => {
        const totalPages = Math.ceil(rowCount / rowsPerPage);
        return (
            <div className="d-flex justify-content-between align-items-center p-3 border-top">
                <div className="d-flex align-items-center gap-2">
                    <span className="text-muted" style={{ fontSize: "0.875rem" }}>Rows per page:</span>
                    <select
                        className="form-select form-select-sm"
                        style={{ width: "80px" }}
                        value={rowsPerPage}
                        onChange={(e) => onChangeRowsPerPage(Number(e.target.value), currentPage)}
                    >
                        {[5, 10, 15, 20].map((option) => (
                            <option key={option} value={option}>{option}</option>
                        ))}
                    </select>
                </div>

                <div className="d-flex align-items-center gap-1">
                    <button
                        className="btn btn-sm border"
                        onClick={() => onChangePage(currentPage - 1, rowCount)}
                        disabled={currentPage === 1}
                    >
                        <i className="ri-arrow-left-s-line"></i>
                    </button>
                    <span className="mx-3 text-muted" style={{ fontSize: "0.875rem" }}>
                        Page {currentPage} of {totalPages}
                    </span>
                    <button
                        className="btn btn-sm border"
                        onClick={() => onChangePage(currentPage + 1, rowCount)}
                        disabled={currentPage === totalPages}
                    >
                        <i className="ri-arrow-right-s-line"></i>
                    </button>
                </div>
            </div>
        );
    };

    const columns = [
        { name: "S.No", cell: (row, index) => index + 1 + (currentPage - 1) * rowsPerPage, width: "80px" },
        { name: "Client Name", selector: (row) => row.client_name || "", sortable: true },
        { name: "Assessment Date", selector: (row) => row.assessment_date || "", sortable: true },
        { name: "Total Score", selector: (row) => row.total_score || 0, sortable: true, center: true },
        {
            name: "Anxiety Level",
            cell: (row) => {
                let badgeClass = "bg-primary-subtle text-primary";
                if (row.anxiety_level?.includes("Low")) badgeClass = "bg-success-subtle text-success";
                if (row.anxiety_level?.includes("Moderate")) badgeClass = "bg-warning-subtle text-warning";
                if (row.anxiety_level?.includes("High")) badgeClass = "bg-danger-subtle text-danger";
                return <span className={`badge ${badgeClass} rounded-pill px-3 py-2`}>{row.anxiety_level}</span>;
            },
            sortable: true,
            center: true
        },
        { name: "Assessed By", selector: (row) => row.assessed_by || "", sortable: true },
        {
            name: "Actions",
            cell: (row) => (
                <div className="d-flex justify-content-center gap-1">
                    {onEditAnxiety && (
                        <button className="btn btn-sm btn-outline-primary" onClick={() => onEditAnxiety(row.id)}>
                            <i className="ri-edit-line"></i>
                        </button>
                    )}
                    {onDeleteAnxiety && (
                        <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(row.id)}>
                            <i className="ri-delete-bin-line"></i>
                        </button>
                    )}
                </div>
            ),
            width: "140px",
            ignoreRowClick: true,
            button: true,
        },
    ];

    // Show loading or error
    if (isLoading) {
        return <div className="text-center p-5">Loading anxiety assessments...</div>;
    }

    if (error) {
        return <div className="text-center p-5 text-danger">Error: {error}</div>;
    }

    return (
        <div>
            <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
                <div className="d-flex align-items-center gap-2">
                    <div className="dropdown">
                        <button className="btn btn-label-primary btn-sm dropdown-toggle" data-bs-toggle="dropdown">
                            <i className="ri-download-2-line me-1"></i> Export
                        </button>
                        <ul className="dropdown-menu shadow-sm">
                            <li><button className="dropdown-item" onClick={handleExportPDF}><i className="ri-file-pdf-line me-1 text-danger"></i> PDF</button></li>
                            <li>
                                <CSVLink data={filteredRecords} filename="bai_assessments.csv" className="dropdown-item">
                                    <i className="ri-file-excel-line me-1 text-success"></i> CSV
                                </CSVLink>
                            </li>
                        </ul>
                    </div>

                    <input
                        type="text"
                        placeholder="Search BAI assessments..."
                        className="form-control form-control-sm"
                        style={{ width: "280px" }}
                        value={filterText}
                        onChange={(e) => setFilterText(e.target.value)}
                    />
                </div>

                <h6 className="mb-0 text-center text-muted flex-grow-1">
                    Showing {start}-{end} of {totalRows} records
                </h6>

                <div>
                    {onAddAnxiety && (
                        <button className="btn btn-sm btn-primary" onClick={onAddAnxiety}>
                            <i className="ri-add-line me-1"></i> Add New
                        </button>
                    )}
                </div>
            </div>

            <DataTable
                columns={columns}
                data={filteredRecords}
                pagination
                paginationPerPage={rowsPerPage}
                paginationTotalRows={totalRows}
                onChangeRowsPerPage={setRowsPerPage}
                onChangePage={setCurrentPage}
                paginationComponent={CustomPagination}
                highlightOnHover
                striped
                responsive
                noDataComponent={
                    <div className="p-5 text-center text-muted">
                        <i className="ri-file-list-3-line fs-1 d-block mb-2"></i>
                        No Anxiety Form found
                    </div>
                }
            />
        </div>
    );
};

export default AnxietyList;
